import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, Field } from "../../ui";
import type { Schemas } from "../workspace/api.generated";
import { failure } from "../workspace/behavior";
import { money } from "../workspace/model";
import { getTransactionDetail } from "./api";
import { RecipientAccountReveal } from "./RecipientAccountReveal";
import { TransactionSummaryDialog } from "./components/TransactionSummaryDialog";

export function TransactionSummaryModal({
  transactionId,
  onClose,
}: {
  readonly transactionId: number;
  readonly onClose: () => void;
}) {
  const { apiClient, queryClient } = useAdminApplication();
  const session = useAdminSession();
  const requestCacheKey = [
    "admin",
    session.sessionEpoch,
    "card-cancellation-request",
    transactionId,
  ] as const;
  const cachedRequest = queryClient.getQueryData<{
    key: string;
    reason: string;
    outcome: "none" | "success" | "failed" | "unknown";
  }>(requestCacheKey);
  const [stage, setStage] = useState<"detail" | "reason" | "confirm">("detail");
  const [reason, setReason] = useState(cachedRequest?.reason ?? "");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  const [outcome, setOutcome] = useState<
    "none" | "success" | "failed" | "unknown"
  >(cachedRequest?.outcome ?? "none");
  const requestRef = useRef<{ key: string; reason: string } | null>(
    cachedRequest ?? null,
  );
  const busyRef = useRef(false);
  const query = useQuery({
    queryKey: [
      "admin",
      session.sessionEpoch,
      "transaction-summary",
      transactionId,
    ],
    queryFn: ({ signal }) =>
      getTransactionDetail(apiClient, transactionId, signal),
    staleTime: 0,
    retry: false,
  });
  const action = query.data?.availableActions?.find(
    (row) => row.code === "CARD_PAYMENT_CANCEL",
  );
  const permitted = Boolean(
    action?.allowed &&
    session.authSession?.authorities?.includes("PAYMENT_CANCEL"),
  );
  const payment = query.data?.paymentSummary;
  const amount =
    payment?.approvedAmount == null
      ? null
      : payment.approvedAmount - (payment.canceledAmount ?? 0);
  const recordOutcome = (value: typeof outcome) => {
    setOutcome(value);
    if (requestRef.current)
      queryClient.setQueryData(requestCacheKey, {
        ...requestRef.current,
        outcome: value,
      });
  };
  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["admin", session.sessionEpoch],
    });
    const result = await query.refetch();
    if (result.data?.transaction.paymentStatus === "CANCELED") {
      recordOutcome("success");
      setMessage("카드 결제가 취소되었습니다.");
      setStage("detail");
    } else if (
      result.data?.cancellationStatus === "FAILED" &&
      result.data.transaction.paymentStatus === "APPROVED" &&
      result.data.availableActions.some(
        (item) => item.code === "CARD_PAYMENT_CANCEL" && item.allowed,
      )
    ) {
      requestRef.current = null;
      queryClient.removeQueries({ queryKey: requestCacheKey, exact: true });
      setOutcome("failed");
      setStage("detail");
      setMessage(
        "이전 취소가 실패한 것으로 확인됐습니다. 사유와 최신 금액을 확인한 뒤 새 취소 요청을 할 수 있습니다.",
      );
    }
  };
  const cancel = async () => {
    if (
      busyRef.current ||
      !permitted ||
      stage !== "confirm" ||
      outcome === "unknown"
    )
      return;
    const text = reason.trim();
    if (
      text.length < 5 ||
      text.length > 1000 ||
      amount == null ||
      amount <= 0
    ) {
      setMessage("취소 사유는 5~1,000자이며 취소금액 정보가 필요합니다.");
      return;
    }
    requestRef.current ??= {
      key: globalThis.crypto.randomUUID(),
      reason: text,
    };
    queryClient.setQueryData(requestCacheKey, {
      ...requestRef.current,
      outcome: "unknown",
    });
    busyRef.current = true;
    setPending(true);
    setMessage("");
    try {
      const { data } = await apiClient.post<
        Schemas["AdminPaymentCancellationView"]
      >(
        `/api/v1/admin/bo/transactions/${transactionId}/card-payment:cancel`,
        { reason: requestRef.current.reason },
        {
          headers: { "Idempotency-Key": requestRef.current.key },
          cache: "no-store",
        },
      );
      if (
        data.status === "COMPLETED" ||
        data.status === "CANCELED" ||
        data.paymentStatus === "CANCELED"
      ) {
        recordOutcome("success");
        setMessage("카드 결제가 취소되었습니다.");
        setStage("detail");
      } else if (data.status === "FAILED") {
        recordOutcome("failed");
        setMessage(
          "결제 취소가 실패했습니다. 최신 결과와 실패 사유를 확인해 주세요.",
        );
      } else {
        recordOutcome("unknown");
        setMessage(
          "취소 결과가 확정되지 않았습니다. 다시 요청하지 말고 최신 결과를 조회해 주세요.",
        );
      }
      await queryClient.invalidateQueries({
        queryKey: ["admin", session.sessionEpoch],
      });
    } catch (error) {
      const code = (error as { status?: number }).status;
      recordOutcome(
        !failure(error).reconcile &&
          code &&
          [400, 401, 403, 404, 422].includes(code)
          ? "failed"
          : "unknown",
      );
      setMessage(failure(error).message);
    } finally {
      busyRef.current = false;
      setPending(false);
    }
  };
  return (
    <TransactionSummaryDialog
      open
      detail={query.data}
      state={query.isPending ? "loading" : query.isError ? "error" : "ready"}
      onClose={onClose}
      busy={pending}
      onRetry={() => {
        void query.refetch();
      }}
      action={
        stage === "detail" &&
        outcome !== "success" &&
        outcome !== "unknown" &&
        action ? (
          <Button
            size="small"
            disabled={
              !permitted || amount == null || amount <= 0 || query.isFetching
            }
            onClick={() => {
              setStage("reason");
              setMessage("");
            }}
          >
            결제 취소 처리
          </Button>
        ) : stage === "reason" ? (
          <Button
            size="small"
            disabled={reason.trim().length < 5 || reason.trim().length > 1000}
            onClick={() => setStage("confirm")}
          >
            취소 내용 확인
          </Button>
        ) : stage === "confirm" && outcome !== "unknown" ? (
          <Button
            size="small"
            isLoading={pending}
            disabled={!permitted || query.isFetching}
            onClick={() => {
              void cancel();
            }}
          >
            전액 취소 확정
          </Button>
        ) : undefined
      }
    >
      {query.data && stage === "detail" ? (
        <RecipientAccountReveal
          key={transactionId}
          transactionId={transactionId}
        />
      ) : null}
      {action && !permitted && outcome !== "success" ? (
        <p>
          {action.blockedReasonMessage ??
            "현재 권한과 거래 상태에서는 결제를 취소할 수 없습니다."}
        </p>
      ) : null}
      {permitted && (amount == null || amount <= 0) ? (
        <p>
          취소할 승인 잔액이 확인되지 않아 결제를 취소할 수 없습니다. 최신 결제
          정보를 확인해 주세요.
        </p>
      ) : null}
      {stage !== "detail" ? (
        <section aria-label="카드 전액 취소 확인">
          <p>전액 취소금액: {amount == null ? "정보 없음" : money(amount)}</p>
          <Field
            label="취소 사유"
            control="textarea"
            value={reason}
            maxLength={1000}
            disabled={pending || Boolean(requestRef.current)}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
          {stage === "confirm" ? (
            <p>
              카드 승인을 전액 취소합니다. 금액과 사유를 확인한 뒤 확정해
              주세요.
            </p>
          ) : null}
          {!requestRef.current ? (
            <Button
              size="small"
              variant="quiet"
              onClick={() => setStage("detail")}
            >
              취소하지 않고 돌아가기
            </Button>
          ) : null}
        </section>
      ) : null}
      {message ? (
        <Alert
          persistent
          title={
            outcome === "success"
              ? "결제 취소 완료"
              : outcome === "unknown"
                ? "취소 결과 확인 필요"
                : "취소 요청 안내"
          }
          variant={outcome === "success" ? "success" : "warning"}
        >
          {message}
        </Alert>
      ) : null}
      {outcome === "unknown" || outcome === "failed" ? (
        <Button
          size="small"
          variant="secondary"
          disabled={query.isFetching || pending}
          onClick={() => {
            void refresh();
          }}
        >
          최신 결과 조회
        </Button>
      ) : null}
    </TransactionSummaryDialog>
  );
}
